
import { createApp, defineCustomElement, getCurrentInstance, h } from 'vue'
import axios from '@/plugins/axios';
import vuetify from '@/plugins/vuetify';
import i18n from '@/plugins/i18n';
import {createPinia} from 'pinia';
import appPlugin from '@/plugins/app-plugin';
import {setupCalendar} from 'v-calendar';
import {get as _get} from 'lodash-es';

// import styles
import 'v-calendar/style.css';
import '@/styles/tailwind.css';
import '@/styles/global.css';

/**
 * defineCustomElement not work
 * there is a bug with styles of subcomponents
 */

function parseComponentData(elem){
	const componentData = {...elem.dataset};

	for (const key in componentData) {
		if (!Object.hasOwnProperty.call(componentData, key)) continue;
		if(typeof componentData[key] === 'string'){
			componentData[key] = JSON.parse(componentData[key]);
		}
	}

	return componentData;
}

function installWebComponent(component, plugins = [], selector = '#app', config = null){
	const elem = typeof(selector) === 'string' ? document.querySelector(selector) : selector;

	if(!elem) return;

	const componentData = config || parseComponentData(elem);

	const app = createApp(component, componentData);

	if(plugins){
		plugins.forEach(plugin => {
			let options = undefined;
			if(typeof plugin.parseOptions === 'function') options = plugin.parseOptions(componentData);
			app.use(plugin, options);
		});
	}

	app.config.devtools = true;
	app.config.performance = true;
	app.config.unwrapInjectedRef = true;

	app.mount(selector);
}

function init(MainComponent, config, elementSelector = '#trenitalia'){
	installWebComponent(MainComponent, [createPinia(), axios, i18n, {
		install: (app) => {
			if(app.config.globalProperties.$pinia){
				app.config.globalProperties.$pinia.use(() => ({$i18n: i18n.global}));
			}
		},
	}, vuetify, {install:setupCalendar}, appPlugin], elementSelector, config);
}

export function start(MainComponent, startConf){
	let config = sessionStorage.getItem(_get(startConf, 'configStorageKey', 'trenitalia-post-sale-config'));
	config = JSON.parse(config);
	init(MainComponent, config, _get(startConf, 'el') || _get(startConf, 'elementSelector', '#trenitalia'));

	if(window.self != window.top){
		window.document.body.addEventListener('resize', function(){
			window.parent.postMessage('resize');
		});
		window.addEventListener('resize', function(){
			window.parent.postMessage('resize');
		});
		window.parent.postMessage('resize');
	}
}

function getFontUrl(path, manifest){
	if(import.meta.env.DEV) return import.meta.env.BASE_URL+path;
	if(!manifest[path].file) return import.meta.env.BASE_URL+path;
	return import.meta.env.BASE_URL+manifest[path].file;
}

/**
 * font-face not work in shadowDOM
 * this workaround add the font definition in document head
 * @param {any} manifest 
 */
function appendCustomStyle(manifest){
	let style = document.createElement('style');
	style.appendChild(document.createTextNode(`
		html{font-size:1rem;}/* reset font size */
		/* MaterialDesignIcons.com */
		@font-face{font-family:Material Design Icons;src:url(${getFontUrl('node_modules/@mdi/font/fonts/materialdesignicons-webfont.eot', manifest)}?v=7.1.96);src:url(${getFontUrl('node_modules/@mdi/font/fonts/materialdesignicons-webfont.eot', manifest)}?#iefix&v=7.1.96) format("embedded-opentype"),url(${getFontUrl('node_modules/@mdi/font/fonts/materialdesignicons-webfont.woff2', manifest)}?v=7.1.96) format("woff2"),url(${getFontUrl('node_modules/@mdi/font/fonts/materialdesignicons-webfont.woff', manifest)}?v=7.1.96) format("woff"),url(${getFontUrl('node_modules/@mdi/font/fonts/materialdesignicons-webfont.ttf', manifest)}?v=7.1.96) format("truetype");font-weight:400;font-style:normal}
	`));
	document.head.appendChild(style);
}

export function createCustomElement(component, config){
	return defineCustomElement({
		setup(){
			return () => [
				h('div', {ref: 'appContainer'})
			]
		},
		methods:{
			async loadStyles(){
				if(import.meta.env.DEV){
					// copy style from head to shadowDOM of the custom element
					Array.from(document.head.getElementsByTagName('style')).forEach(script => {
						this.$el.parentNode.appendChild(script);
					});

					appendCustomStyle();
				}else{
					// import style in shadowDOM using manifest
					return fetch(import.meta.env.BASE_URL+'manifest.json')
					.then(resp => resp.json())
					.then(manifest => {
						if(!manifest) return;
						return new Promise((resolve, reject) => {
							appendCustomStyle(manifest);

							if(manifest['style.css']){
								const link = document.createElement('link');
								link.type = 'text/css';
								link.rel = 'stylesheet';
								link.onload = () => {
									resolve();
								};
								link.href = import.meta.env.BASE_URL+manifest['style.css'].file;
								this.$el.parentNode.appendChild(link);
							}else{
								resolve();
							}
						});
					}).catch(e => {
						// ignore ??
					});
				}
			},
		},
		mounted(){
			this.loadStyles()
			.then(() => {
				/**
				 * Vuetify dinamically add some blocks to head and body
				 * This catch added nodes and move these in the shadowDOM root
				 */
				const mutationObserver = new MutationObserver((mutationsList) => {
					for (const mutation of mutationsList) {
						if(mutation.type == 'childList' && mutation.addedNodes){
							for (const node of mutation.addedNodes) {
								if(node.className == 'v-overlay-container' || node.id == 'vuetify-theme-stylesheet'){
									this.$el.parentNode.appendChild(node);
								}
							}
						}
					}
				});
				mutationObserver.observe(document.body,  {childList: true});
				mutationObserver.observe(document.head,  {childList: true});

				start(component, {
					configStorageKey: _get(config, 'configStorageKey'),
					el: this.$refs.appContainer
				});
			});
		}
	})
}